import React, {
  forwardRef,
  useCallback,
  ForwardedRef,
  RefObject,
  createRef,
  useRef,
  useImperativeHandle,
  useEffect,
} from 'react';
import { Button, Col, FormInstance, Row } from 'rsuite';
import { SingleOwnersForm } from './SingleOwnersForm';
import { FormModelPerson } from 'src/types/person';
import { getInitialFormModelValues } from 'src/common/people';

interface OwnersFormProps {
  disabled?: boolean;
  disableSimpleEdition?: boolean;
  multipleOwners?: boolean;
  onChange: (value: FormModelPerson[]) => void;
  propertyAddress?: string;
  values: FormModelPerson[];
}

export interface OwnersFormRef {
  validateForm: () => boolean;
}

export const OwnersForm = forwardRef<OwnersFormRef, OwnersFormProps>(
  (
    {
      disabled = false,
      disableSimpleEdition = false,
      multipleOwners = false,
      onChange,
      propertyAddress = '',
      values,
    }: OwnersFormProps,
    ref: ForwardedRef<OwnersFormRef>
  ) => {
    const tempRef = useRef<
      Record<string, RefObject<FormInstance<FormModelPerson> | null>>
    >({});

    useImperativeHandle(ref, () => ({
      validateForm: () => {
        return Object.values(tempRef.current).every((formRef) => {
          return formRef.current?.check();
        });
      },
    }));

    useEffect(() => {
      // Create a new refs object to avoid stale closures
      const newRefs: Record<
        string,
        RefObject<FormInstance<FormModelPerson> | null>
      > = {};

      values.forEach((_, index) => {
        const key = `owner_${index}`;
        // Only create new ref if it doesn't exist
        if (!tempRef.current[key]) {
          newRefs[key] = createRef<FormInstance<FormModelPerson>>();
        } else {
          newRefs[key] = tempRef.current[key];
        }
      });

      tempRef.current = newRefs;
    }, [values.length]);

    const handleChange = useCallback(
      (value: FormModelPerson, index: number) => {
        const newValue = [...values];
        newValue[index] = value;
        onChange(newValue);
      },
      [values, onChange]
    );

    const handleRemoveOwner = useCallback(
      (position: number) => {
        const newValues = values.filter((_, index) => index !== position);
        onChange(newValues);

        // Clean up refs properly - remove the deleted ref and reindex remaining ones
        const newRefs: Record<
          string,
          RefObject<FormInstance<FormModelPerson> | null>
        > = {};
        newValues.forEach((_, index) => {
          const oldKey = `owner_${index >= position ? index + 1 : index}`;
          const newKey = `owner_${index}`;
          if (tempRef.current[oldKey]) {
            newRefs[newKey] = tempRef.current[oldKey];
          }
        });
        tempRef.current = newRefs;
      },
      [values, onChange]
    );

    const handleAddOwner = useCallback(() => {
      const newOwner = getInitialFormModelValues();
      const newKey = `owner_${values.length}`;
      tempRef.current[newKey] = createRef<FormInstance<FormModelPerson>>();
      onChange([...values, newOwner]);
    }, [values, onChange]);

    return (
      <>
        {values.map((value: FormModelPerson, index: number) => {
          const refKey = `owner_${index}`;
          const formRef = tempRef.current[refKey];

          if (!formRef) {
            console.warn(`Ref for ${refKey} not found, creating new one`);
            tempRef.current[refKey] =
              createRef<FormInstance<FormModelPerson>>();
          }

          return (
            <SingleOwnersForm
              key={refKey}
              disabled={!disableSimpleEdition && disabled}
              onChange={(value) => handleChange(value, index)}
              onRemove={() => handleRemoveOwner(index)}
              propertyAddress={propertyAddress}
              ref={tempRef.current[refKey]}
              showRemove={index > 0}
              title={
                index === 0 ? 'Owner Details' : `Owner ${index + 1} Details`
              }
              formValues={value}
              disabledFields={{
                email: disableSimpleEdition,
                firstName: disableSimpleEdition,
                lastName: disableSimpleEdition,
              }}
            />
          );
        })}
        {multipleOwners && (
          <Row>
            <Col>
              <Button
                className="btn-tertiary mt-16 mb-8"
                onClick={handleAddOwner}
                disabled={disabled || disableSimpleEdition}
              >
                Add additional owner
              </Button>
            </Col>
          </Row>
        )}
      </>
    );
  }
);
